import { useCallback, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { getDevEmail, getDevSub } from '@/auth/devAuth';
import { AUTH_MODE } from '@/auth/authMode';
import { useLocalAuthContext } from '@/auth/LocalAuthProvider';

export interface UserProfile {
  sub?: string;
  email?: string;
  name?: string;
  [claim: string]: unknown;
}

export interface UseAuthResult {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | undefined;
  accessToken: string | null;
  profile: UserProfile | undefined;
  email: string | undefined;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

/** Dev-bypass implementation: an always-authenticated local identity. */
function useDevAuth(): UseAuthResult {
  const noop = useCallback(async () => {}, []);
  const email = getDevEmail();
  return useMemo<UseAuthResult>(
    () => ({
      isLoading: false,
      isAuthenticated: true,
      error: undefined,
      accessToken: null,
      profile: { sub: getDevSub(), email },
      email,
      signIn: noop,
      signOut: noop,
    }),
    [noop, email],
  );
}

/**
 * Convenience wrapper over react-oidc-context's hook. Exposes the bits the app
 * cares about and implements a Cognito-aware sign-out.
 */
function useOidcAuthResult(): UseAuthResult {
  const auth = useOidcAuth();

  const signIn = useCallback(async () => {
    await auth.signinRedirect();
  }, [auth]);

  const signOut = useCallback(async () => {
    // Clear the local session first. The post-logout landing goes through
    // ProtectedRoute to /login; nothing signs back in automatically.
    await auth.removeUser();

    const domain = import.meta.env.VITE_COGNITO_DOMAIN;
    const logoutUri = import.meta.env.VITE_COGNITO_LOGOUT_URI;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

    if (domain) {
      // Cognito Hosted UI logout endpoint.
      const params = new URLSearchParams({
        client_id: clientId,
        logout_uri: logoutUri,
      });
      window.location.assign(`${domain.replace(/\/$/, '')}/logout?${params.toString()}`);
      return;
    }

    // Fall back to OIDC end-session, then to a plain redirect.
    try {
      await auth.signoutRedirect();
    } catch {
      window.location.assign(logoutUri);
    }
  }, [auth]);

  return useMemo<UseAuthResult>(
    () => ({
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      error: auth.error,
      accessToken: auth.user?.access_token ?? null,
      profile: auth.user?.profile as UserProfile | undefined,
      email: (auth.user?.profile as UserProfile | undefined)?.email,
      signIn,
      signOut,
    }),
    [auth.isLoading, auth.isAuthenticated, auth.error, auth.user, signIn, signOut],
  );
}

/** Local credential implementation: state comes from the LocalAuthProvider. */
function useLocalAuth(): UseAuthResult {
  const ctx = useLocalAuthContext();
  // Sign-in is driven by the dedicated form (useLocalAuthContext.signIn), so the
  // generic, argument-less signIn is a no-op here.
  const signIn = useCallback(async () => {}, []);
  return useMemo<UseAuthResult>(
    () => ({
      isLoading: false,
      isAuthenticated: ctx.isAuthenticated,
      error: undefined,
      accessToken: ctx.token,
      profile: ctx.user
        ? { sub: ctx.user.id, email: ctx.user.email, name: ctx.user.name ?? undefined }
        : undefined,
      email: ctx.user?.email,
      signIn,
      signOut: ctx.signOut,
    }),
    [ctx, signIn],
  );
}

// Pick the implementation once at module load (the mode never changes at
// runtime), keeping hook call order stable across renders.
export const useAuth: () => UseAuthResult =
  AUTH_MODE === 'dev' ? useDevAuth : AUTH_MODE === 'local' ? useLocalAuth : useOidcAuthResult;
